/**
 * Refer Quote Panel (PR #5d).
 *
 * Displayed in the right-hand sidebar of the Commercial PA workbench
 * when the user holds `quotes.refer` but cannot bind (either they lack
 * `quotes.bind` or a bind attempt returned `bind_authority_exceeded`).
 *
 * The user fills in an optional note and clicks "Refer for approval".
 * The panel then shows a confirmation with the assigned referral ID
 * and the queue status so the broker knows what happens next.
 */

import React, { useState } from 'react';
import type { AppliedFacility, QuoteReferRequest, QuoteReferResponse } from '@cortex/domain';
import { formatMoney } from '@/components/ui/CurrencyValue';

interface ReferQuotePanelProps {
  readonly context: Record<string, unknown>;
  readonly appliedFacility: AppliedFacility | null;
  readonly grossPremium: number;
  readonly quoteRef: string;
  readonly onRefer: (req: QuoteReferRequest) => Promise<QuoteReferResponse>;
  readonly onNewQuote: () => void;
}

export const ReferQuotePanel: React.FC<ReferQuotePanelProps> = ({
  context,
  appliedFacility,
  grossPremium,
  quoteRef,
  onRefer,
  onNewQuote,
}) => {
  const [isReferring, setIsReferring] = useState(false);
  const [confirmation, setConfirmation] = useState<QuoteReferResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleRefer = async () => {
    setIsReferring(true);
    setErrorMessage(null);
    try {
      const req: QuoteReferRequest = {
        context: context as QuoteReferRequest['context'],
        appliedFacility,
      };
      const result = await onRefer(req);
      setConfirmation(result);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Referral failed. Please retry.';
      setErrorMessage(msg);
    } finally {
      setIsReferring(false);
    }
  };

  if (confirmation) {
    return (
      <section
        aria-label="Quote referral submitted"
        className="rounded-xl border border-amber-200 bg-amber-50 shadow-md overflow-hidden font-sans"
      >
        <div className="bg-amber-600 px-5 py-3 text-white flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-xs font-bold uppercase tracking-widest">Referral Submitted</span>
        </div>
        <div className="p-5 space-y-3">
          <dl className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <dt className="font-bold text-slate-500 uppercase tracking-widest text-micro mb-0.5">
                Quote ref
              </dt>
              <dd className="font-mono text-amber-800 font-bold">
                {confirmation.referral.quoteRef}
              </dd>
            </div>
            <div>
              <dt className="font-bold text-slate-500 uppercase tracking-widest text-micro mb-0.5">
                Gross premium
              </dt>
              <dd className="font-mono text-amber-800 font-bold">
                {formatMoney(confirmation.referral.grossPremium)}
              </dd>
            </div>
            <div className="col-span-2">
              <dt className="font-bold text-slate-500 uppercase tracking-widest text-micro mb-0.5">
                Status
              </dt>
              <dd className="text-slate-700 capitalize">{confirmation.referral.status}</dd>
            </div>
            <div className="col-span-2">
              <dt className="font-bold text-slate-500 uppercase tracking-widest text-micro mb-0.5">
                Referral ID
              </dt>
              <dd className="font-mono text-xs text-slate-500 break-all">
                {confirmation.referral.id}
              </dd>
            </div>
          </dl>
          <p className="text-xs text-slate-500 leading-relaxed">
            A Lombard underwriter will review this referral and respond. You can check the status in
            the Referrals queue.
          </p>
          <button
            type="button"
            onClick={onNewQuote}
            className="mt-2 w-full rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-50 transition-colors"
          >
            Start new quote
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      aria-label="Refer quote for approval"
      className="rounded-xl border border-amber-200 bg-white shadow-md overflow-hidden font-sans"
    >
      <div className="bg-amber-600/95 px-5 py-3 text-white flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-widest">Refer for approval</span>
        <span className="text-micro font-mono text-white/60">{quoteRef}</span>
      </div>
      <div className="p-5 space-y-4">
        <div>
          <p className="text-micro font-bold text-slate-500 uppercase tracking-widest mb-1">
            Gross premium
          </p>
          <p className="text-xl font-bold text-amber-700 tabular-nums tracking-tight">
            {formatMoney(grossPremium)}
          </p>
        </div>

        <p className="text-xs text-slate-600 leading-relaxed">
          This quote exceeds your binding authority. Submit it to a Lombard underwriter for review
          and approval.
        </p>

        {errorMessage && (
          <div className="rounded border border-destructive/20 bg-destructive/10 p-3 text-xs text-destructive">
            {errorMessage}
          </div>
        )}

        <button
          type="button"
          onClick={handleRefer}
          disabled={isReferring}
          className="w-full rounded-md bg-amber-600 px-4 py-2 text-xs font-bold text-white uppercase tracking-widest hover:bg-amber-700 disabled:opacity-50 transition-colors"
        >
          {isReferring ? 'Submitting…' : 'Refer for approval'}
        </button>
      </div>
    </section>
  );
};
